import React, { useState } from 'react';

const AddVendor = () => {
  const [formData, setFormData] = useState({
    vendorId: '',
    vendorCategory: '',
    companyName: '',
    contactPerson: '',
    email: '',
    phoneNumber: '',
    physicalAddress: '',
    billingAddress: '',
    website: '',
    productSpecialization: '',
    paymentTerms: '',
    status: 'Active'
  });

  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const vendorCategories = [
    'Precious Metal',
    'Diamonds',
    'Gemstones',
    'Finished Jewellery',
    'Packaging',
    'Appraisal Services',
    'Security Systems',
    'Software',
    'Other'
  ];

  const paymentTermsOptions = [
    'Net 30',
    'Net 60',
    '50% Advance, 50% on Delivery',
    '100% Advance',
    '100% on Delivery',
    'Custom Terms'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    try {
      // API call to backend
      const response = await fetch('/api/vendors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        setMessage('Vendor added successfully!');
        // Reset form
        setFormData({
          vendorId: '',
          vendorCategory: '',
          companyName: '',
          contactPerson: '',
          email: '',
          phoneNumber: '',
          physicalAddress: '',
          billingAddress: '',
          website: '',
          productSpecialization: '',
          paymentTerms: '',
          status: 'Active'
        });
      } else {
        throw new Error('Failed to add vendor');
      }
    } catch (error) {
      setMessage('Error adding vendor. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateVendorId = () => {
    const categories = {
      'Precious Metal': 'GLD',
      'Diamonds': 'DIA',
      'Gemstones': 'GEM',
      'Finished Jewellery': 'FJ',
      'Packaging': 'PKG',
      'Appraisal Services': 'APP',
      'Security Systems': 'SEC',
      'Software': 'SOF',
      'Other': 'OTH'
    };
    
    const prefix = categories[formData.vendorCategory] || 'VEN';
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const newVendorId = `${prefix}-${randomNum}`;
    
    setFormData(prevState => ({
      ...prevState,
      vendorId: newVendorId
    }));
  };
   const commonClassName = "w-full h-12 px-5 pr-12 outline-none text-gray-800 placeholder-gray-500 text-sm bg-white rounded-full border border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 shadow-sm";
  

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center border-b pb-4">
        Add New Vendor
      </h2>
      
      {message && (
        <div className={`mb-6 p-4 rounded-lg text-center font-semibold ${
          message.includes('Error') 
            ? 'bg-red-100 text-red-700 border border-red-300' 
            : 'bg-green-100 text-green-700 border border-green-300'
        }`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Vendor ID and Category Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="vendorCategory" className="block text-sm font-medium text-gray-700">
              Vendor Category *
            </label>
            <select
              id="vendorCategory"
              name="vendorCategory"
              value={formData.vendorCategory}
              onChange={handleChange}
              className={commonClassName}
              required
            >
              <option value="">Select Category</option>
              {vendorCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="vendorId" className="block text-sm font-medium text-gray-700">
              Vendor ID *
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="vendorId"
                name="vendorId"
                value={formData.vendorId}
                onChange={handleChange}
                placeholder="e.g., GLD-001"
                className={commonClassName}
                required
                readOnly
              />
              <button 
                type="button"
                onClick={generateVendorId}
                disabled={!formData.vendorCategory}
                className="w-full sm:w-48 h-12 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
  
              >
                Generate ID
              </button>
            </div>
          </div>
        </div>

        {/* Company Name and Contact Person */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
              Company Name *
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Legal business name"
              className={commonClassName}
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700">
              Contact Person *
            </label>
            <input
              type="text"
              id="contactPerson"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              placeholder="Primary contact name"
              className={commonClassName}
              required
            />
          </div>
        </div>

        {/* Email and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="primary@company.com"
              className={commonClassName}
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
              className={commonClassName}
              required
            />
          </div>
        </div>

        {/* Physical Address */}
        <div className="space-y-2">
          <label htmlFor="physicalAddress" className="block text-sm font-medium text-gray-700">
            Physical Address *
          </label>
          <textarea
            id="physicalAddress"
            name="physicalAddress"
            value={formData.physicalAddress}
            onChange={handleChange}
            placeholder="Street address of office/warehouse"
            rows="3"
            className={commonClassName}
            required
          />
        </div>

        {/* Billing Address */}
        <div className="space-y-2">
          <label htmlFor="billingAddress" className="block text-sm font-medium text-gray-700">
            Billing Address
          </label>
          <textarea
            id="billingAddress"
            name="billingAddress"
            value={formData.billingAddress}
            onChange={handleChange}
            placeholder="If different from physical address"
            rows="3"
            className={commonClassName}
          />
          <p className="text-sm text-gray-500">Leave blank if same as physical address</p>
        </div>

        {/* Website and Product Specialization */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="website" className="block text-sm font-medium text-gray-700">
              Website
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://example.com"
              className={commonClassName}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="productSpecialization" className="block text-sm font-medium text-gray-700">
              Product Specialization
            </label>
            <input
              type="text"
              id="productSpecialization"
              name="productSpecialization"
              value={formData.productSpecialization}
              onChange={handleChange}
              placeholder="e.g., GIA Certified Diamonds, Italian Gold Chains"
              className={commonClassName}
            />
          </div>
        </div>

        {/* Payment Terms and Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700">
              Payment Terms
            </label>
            <select
              id="paymentTerms"
              name="paymentTerms"
              value={formData.paymentTerms}
              onChange={handleChange}
              className={commonClassName}
            >
              <option value="">Select Payment Terms</option>
              {paymentTermsOptions.map(term => (
                <option key={term} value={term}>{term}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status *
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={commonClassName}
              required
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center pt-6">
          <button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding Vendor...
              </span>
            ) : (
              'Add Vendor'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddVendor;